import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import './utils/Array.find.js';
import './utils/Array.from.js';
import './utils/Array.findIndex.js';
import { Overlay, Input } from '@enterprise-ui/canvas-ui-react';
import classnames from 'classnames';
import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import IconArea from './components/IconArea.js';
import MultipleInput from './components/MultipleInput.js';
import OptionsList from './components/OptionsList.js';
import SingleInput from './components/SingleInput.js';
import containsFilter from './filters/contains.js';
import fuzzyFilter from './filters/fuzzy.js';
import noneFilter from './filters/none.js';
import startsWithFilter from './filters/startsWith.js';
import defaultChipRenderer from './renderers/defaultChipRenderer.js';
import defaultOptionRenderer from './renderers/defaultOptionRenderer.js';
import createVisibleOptionsList from './utils/createVisibleOptionsList.js';
import { updateCustomOptions } from './utils/customOptions.js';
import OptionsManager from './utils/OptionsManager.js';

var OPTIONS_STATUS = {
  FAILED: 'FAILED',
  LOADING: 'LOADING',
  NOT_READY: 'NOT_READY',
  READY: 'READY',
  STATIC: 'STATIC'
};
var Autocomplete = function Autocomplete(_ref) {
  var _value$;
  var _ref$allowCustomInput = _ref.allowCustomInputValue,
    allowCustomInputValue = _ref$allowCustomInput === void 0 ? false : _ref$allowCustomInput,
    _ref$allowSelectAll = _ref.allowSelectAll,
    allowSelectAll = _ref$allowSelectAll === void 0 ? false : _ref$allowSelectAll,
    _ref$location = _ref.location,
    location = _ref$location === void 0 ? 'right' : _ref$location,
    className = _ref.className,
    dataTestId = _ref['data-testid'],
    disabled = _ref.disabled,
    disableFieldInfo = _ref.disableFieldInfo,
    error = _ref.error,
    errorText = _ref.errorText,
    _ref$filter = _ref.filter,
    filter = _ref$filter === void 0 ? fuzzyFilter : _ref$filter,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'default' : _ref$type,
    highlight = _ref.highlight,
    hintText = _ref.hintText,
    id = _ref.id,
    onBlur = _ref.onBlur,
    label = _ref.label,
    loadOnce = _ref.loadOnce,
    maxOptionsReachedText = _ref.maxOptionsReachedText,
    maxSelectedOptions = _ref.maxSelectedOptions,
    maxSelectedOptionsText = _ref.maxSelectedOptionsText,
    _ref$multiselect = _ref.multiselect,
    multiselect = _ref$multiselect === void 0 ? false : _ref$multiselect,
    maxLength = _ref.maxLength,
    _ref$renderChip = _ref.renderChip,
    renderChip = _ref$renderChip === void 0 ? defaultChipRenderer : _ref$renderChip,
    _ref$renderOption = _ref.renderOption,
    renderOption = _ref$renderOption === void 0 ? defaultOptionRenderer : _ref$renderOption,
    rightContent = _ref.rightContent,
    onEnter = _ref.onEnter,
    onUpdate = _ref.onUpdate,
    options = _ref.options,
    optionHeight = _ref.optionHeight,
    _ref$optionsListNoMin = _ref.optionsListNoMinWidth,
    optionsListNoMinWidth = _ref$optionsListNoMin === void 0 ? false : _ref$optionsListNoMin,
    placeholder = _ref.placeholder,
    required = _ref.required,
    _ref$retainSearchText = _ref.retainSearchText,
    retainSearchText = _ref$retainSearchText === void 0 ? false : _ref$retainSearchText,
    _ref$selectFirstOnEnt = _ref.selectFirstOnEnter,
    selectFirstOnEnter = _ref$selectFirstOnEnt === void 0 ? false : _ref$selectFirstOnEnt,
    _ref$showSearchIcon = _ref.showSearchIcon,
    showSearchIcon = _ref$showSearchIcon === void 0 ? false : _ref$showSearchIcon,
    propsValue = _ref.value,
    _ref$noResultsMessage = _ref.noResultsMessage,
    noResultsMessage = _ref$noResultsMessage === void 0 ? 'No matching results' : _ref$noResultsMessage,
    _ref$grow = _ref.grow,
    grow = _ref$grow === void 0 ? false : _ref$grow;
  var autocompleteInputRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isFocused = _useState2[0],
    setIsFocused = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isShowingOptions = _useState4[0],
    setIsShowingOptions = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    availableOptions = _useState6[0],
    setAvailableOptions = _useState6[1];
  var _useState7 = useState([]),
    _useState8 = _slicedToArray(_useState7, 2),
    customOptions = _useState8[0],
    setCustomOptions = _useState8[1];
  var optionsStatus = useRef(OPTIONS_STATUS.NOT_READY);
  var _useState9 = useState(null),
    _useState10 = _slicedToArray(_useState9, 2),
    preferredOption = _useState10[0],
    setPreferredOption = _useState10[1];
  var _useState11 = useState(''),
    _useState12 = _slicedToArray(_useState11, 2),
    searchText = _useState12[0],
    setSearchText = _useState12[1];
  var _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isLoading = _useState14[0],
    setIsLoading = _useState14[1];
  var _useState15 = useState(),
    _useState16 = _slicedToArray(_useState15, 2),
    value = _useState16[0],
    setValue = _useState16[1];
  var latestAsyncOptionsIndex = useRef(0);
  var _useState17 = useState([]),
    _useState18 = _slicedToArray(_useState17, 2),
    visibleOptions = _useState18[0],
    setVisibleOptions = _useState18[1];
  var _useState19 = useState(false),
    _useState20 = _slicedToArray(_useState19, 2),
    maxOptionsReached = _useState20[0],
    setMaxOptionsReached = _useState20[1];
  var _useState21 = useState(''),
    _useState22 = _slicedToArray(_useState21, 2),
    screenReaderRemoveOptionText = _useState22[0],
    setScreenReaderRemoveOptionText = _useState22[1];

  //ref to be used in getVisibleOptions to avoid triggering rerender
  var availableOptionsRef = useRef([]);
  availableOptionsRef.current = availableOptions;
  if (allowSelectAll && !multiselect) {
    console.error("to use the 'allowSelectAll' prop 'multiselect' must also be true. Selecting all is only an option on multiselect");
  }
  var visibleOptionsHandler = useMemo(function () {
    return createVisibleOptionsList(filter, type);
  }, [filter, type]);
  var getVisibleOptions = useCallback(function () {
    if (maxOptionsReached) {
      setVisibleOptions([]);
      return;
    }
    var visibleOptions = visibleOptionsHandler(searchText, availableOptionsRef.current, allowCustomInputValue, customOptions, allowSelectAll, multiselect);
    setVisibleOptions(visibleOptions);
  }, [allowCustomInputValue, maxOptionsReached, multiselect, searchText, visibleOptionsHandler]);
  // force sets value even if propsValue is changed to null
  useEffect(function () {
    if (allowSelectAll && (propsValue === null || propsValue === void 0 ? void 0 : propsValue.length) === (options === null || options === void 0 ? void 0 : options.length)) {
      var selectedAllValues = propsValue;
      if (type === 'grouped') {
        var selectAllGrouped = {
          name: 'Ungrouped',
          values: [{
            id: 'selectAll',
            label: 'Select All',
            value: 'Select All'
          }]
        };
        selectedAllValues.unshift(selectAllGrouped);
      } else {
        selectedAllValues.unshift({
          id: 'selectAll',
          label: 'Select All',
          value: 'Select All'
        });
      }
      setValue(selectedAllValues);
    } else {
      setValue(propsValue);
    }
  }, [propsValue, options, allowSelectAll]);

  // sets availableOptions, re-runs every time we get new options, or searchText
  // sets initial availableOptions
  useEffect(function () {
    if (Array.isArray(options)) {
      setAvailableOptions(options);
      availableOptionsRef.current = options;
    }
  }, [options, searchText]);

  // gets the options the user should be seeing
  useEffect(function () {
    if (typeof options !== 'function') {
      getVisibleOptions();
    }
  }, [getVisibleOptions, options]);

  // clears searchText if value is null or undefined for the single-select
  // sets searchText if there is a value for the single-select
  useEffect(function () {
    if (!multiselect && !propsValue) {
      setSearchText('');
    } else if (!multiselect && propsValue) {
      setSearchText(propsValue.label);
    } else if (multiselect && !propsValue) {
      // If this is a multiselect with no passed value, set default to empty array. This is for form validation purposes so that it never passes out undefined, always [] if there is no value.
      setValue([]);
    }
  }, [propsValue, multiselect]);

  // determines if max selected options has been reached
  useEffect(function () {
    var maxSelectedBool = multiselect && maxSelectedOptions && maxSelectedOptions === (value && value.length);
    setMaxOptionsReached(maxSelectedBool);
  }, [multiselect, maxSelectedOptions, value]);
  var finalClasses = classnames('C-Autocomplete', {
    isCollapsible: !grow,
    isDisabled: disabled,
    isError: error,
    isFocused: isFocused || isShowingOptions,
    isHighlighted: highlight
  }, className);

  // Get the input component
  var AutocompleteInput = multiselect ? MultipleInput : SingleInput;

  // Do not display placeholder for multiselect with value added
  var displayPlaceholder = useMemo(function () {
    return !multiselect || !value || value.length === 0;
  }, [multiselect, value]);

  // choose maxSelectedText
  var maxSelectedText = useMemo(function () {
    return maxSelectedOptions ? maxSelectedOptionsText || "Select at most ".concat(maxSelectedOptions) : "";
  }, [maxSelectedOptions, maxSelectedOptionsText]);

  // allow for hintText, maxSelectedText, or both
  var hint = useMemo(function () {
    return [hintText, maxSelectedText].filter(Boolean).join(' ');
  }, [hintText, maxSelectedText]);
  var noResultsMessageComputed = useMemo(function () {
    if (maxOptionsReached) {
      return maxOptionsReachedText ? maxOptionsReachedText : "You selected ".concat(maxSelectedOptions, " option").concat(maxSelectedOptions === 1 ? '' : 's', ". Please remove one before selecting another.");
    }
    return noResultsMessage;
  }, [maxOptionsReached, maxSelectedOptions, noResultsMessage, maxOptionsReachedText]);

  /*
    Options Management
  */
  var callUpdate = useCallback(function (nextValue) {
    onUpdate(name || id, nextValue);
  }, [id, onUpdate]);
  var handleOptionSelectMulti = useCallback(function (option) {
    setPreferredOption(null);
    if (!retainSearchText) {
      setSearchText('');
    }
    if (option.length > 0) {
      setValue(option);
      if (allowCustomInputValue) {
        setCustomOptions(updateCustomOptions(option));
      }
      autocompleteInputRef.current.focus();
      var optionsManagerArray = [];
      option.forEach(function (id) {
        optionsManagerArray.push(id);
      });
      setValue(optionsManagerArray);
      callUpdate(optionsManagerArray);
      return;
    }
    if (allowCustomInputValue) {
      var isFunction = typeof options === 'function';
      var isNotInOptions = !isFunction && !options.some(function (checkedValue) {
        return checkedValue.label === option.label;
      });
      var isCustom = typeof option.id === 'string' && option.id.startsWith('@@');
      var isNotVisible = isFunction && isCustom && !customOptions.some(function (v) {
        return v.label === option.label;
      });
      //See if can pull out to function so not dublicating code
      if (isNotInOptions || isNotVisible) {
        setCustomOptions(function (prev) {
          return [].concat(_toConsumableArray(prev), [option]);
        });
      }
    }

    // Selecting checkboxes are losing screen reader priority to the focus, wrapping the focus in a setTimeout so it moves to the next tick.
    setTimeout(function () {
      autocompleteInputRef.current.focus();
    }, 100);
    var nextValue = OptionsManager.add(value, option);
    setValue(nextValue);
    callUpdate(nextValue);
  }, [callUpdate, value, retainSearchText, visibleOptions]);
  var handleOptionSelectSingle = useCallback(function (option) {
    setIsShowingOptions(false);
    setPreferredOption(null);
    setSearchText(option.label);
    callUpdate(option);
  }, [callUpdate]);
  var handleOptionRemove = useCallback(function (option) {
    return function () {
      setScreenReaderRemoveOptionText("".concat(option.label, " Removed"));
      if (option.length > 0) {
        setValue([]);
        setCustomOptions([]);
        callUpdate([]);
        return;
      }

      //Handle if Select All has been called but remove an option from the list so no longer are all selected
      if (value.length <= 2 && value.some(function (checkedValue) {
        return checkedValue.label === 'Select All';
      })) {
        value.shift();
      }
      var nextValue = OptionsManager.remove(value, option);
      setValue(nextValue);
      callUpdate(nextValue);
      if (allowCustomInputValue) {
        var findIndex = customOptions.findIndex(function (a) {
          return a.id === option.id;
        });
        findIndex !== -1 && customOptions.splice(findIndex, 1);
      }
    };
  }, [callUpdate, value]);
  var handleOptionSelect = useCallback(function (option) {
    return function () {
      if (multiselect) {
        if (value !== null && value !== void 0 && value.some(function (checkedValue) {
          return checkedValue.label === option.label || option.length === value.length;
        })) {
          handleOptionRemove(option)();
        } else {
          handleOptionSelectMulti(option);
        }
      } else {
        handleOptionSelectSingle(option);
      }
    };
  }, [handleOptionSelectMulti, handleOptionSelectSingle, multiselect]);
  var handleOptionsClose = useCallback(function () {
    // close options list no matter what
    setIsShowingOptions(false);
    setPreferredOption(null);
    if (multiselect) {
      setSearchText('');
    }
  }, [multiselect]);

  // Handle Key Input
  var handleKeyDown = useCallback(function (event) {
    switch (event.key) {
      case 'Backspace':
      case 'Delete':
        {
          if (!multiselect) break;
          if (searchText.length !== 0) break;
          var lastOption = value[value.length - 1];
          if (lastOption && grow) {
            handleOptionRemove(lastOption)();
          }
          break;
        }

      // Handle confirm & remove selection
      case 'Enter':
        {
          /*
           * Autocomplete will be "focused" when selecting options. If so, cancel the key event and take over.
           * It will not be focused when tabbing to previously selected chips. Allow these key events to occur,
           * this allows for "removing" chips via key board navigation.
           */
          if (isFocused) {
            event.preventDefault();
          }
          if (preferredOption && !preferredOption.disabled) {
            event.preventDefault();
            if (preferredOption.label === 'Select All') {
              if (type === 'grouped') {
                var groupValues = [];
                visibleOptions.map(function (option) {
                  groupValues = groupValues.concat(option.values);
                });
                handleOptionSelect(groupValues)();
              } else {
                handleOptionSelect(visibleOptions)();
              }
            } else {
              handleOptionSelect(preferredOption)();
            }
          } else if (typeof onEnter === 'function') {
            // A user has entered a value, but not selected an option, and hit "enter"
            event.preventDefault();
            onEnter(event, value);
          } else if (selectFirstOnEnter) {
            // A user has entered a value, not selected an option, and no onEnter function was passed,
            // so we get the first value in the visible options list
            if (visibleOptions.length > 0) {
              event.preventDefault();
              var firstOption = visibleOptions[0];
              if (type === 'grouped') {
                if (firstOption.values[0].label === 'Select All') {
                  var _groupValues = [];
                  visibleOptions.map(function (option) {
                    _groupValues = _groupValues.concat(option.values);
                  });
                  handleOptionSelect(_groupValues)();
                } else {
                  handleOptionSelect(firstOption.values[0])();
                }
              } else if (firstOption.label === 'Select All') {
                handleOptionSelect(visibleOptions)();
              } else {
                handleOptionSelect(firstOption)();
              }
            }
          }
          break;
        }

      // Changing selected input
      case 'ArrowDown':
      case 'ArrowUp':
        {
          event.preventDefault();
          if (visibleOptions.length === 0) break;

          // if this is grouped, we have to flatten it in order to see which option is next
          var flattenedOptions = [];
          if (type === 'grouped') {
            visibleOptions.map(function (group) {
              group.values.map(function (option) {
                flattenedOptions.push(option);
              });
            });
          }
          var currentIndex = -1;
          if (preferredOption !== null) {
            currentIndex = OptionsManager.findIndex(type === 'grouped' ? flattenedOptions : visibleOptions, preferredOption);
          }
          var step = event.key === 'ArrowDown' ? 1 : -1;
          var nextOption = type === 'grouped' ? flattenedOptions[currentIndex + step] : visibleOptions[currentIndex + step];
          if (nextOption) {
            setPreferredOption(nextOption);
          }
          break;
        }

      // Handle leaving field
      case 'Escape':
      case 'Tab':
        {
          handleOptionsClose();
          break;
        }
    }
  }, [handleOptionRemove, handleOptionSelect, handleOptionsClose, isFocused, multiselect, preferredOption, onEnter, searchText, selectFirstOnEnter, value, visibleOptions, type, grow]);

  // Renderers
  var handleChipRender = useCallback(function (option) {
    return renderChip(option, handleOptionRemove(option), option.disabled || disabled, option.id);
  }, [renderChip, handleOptionRemove, disabled]);
  var prepareOptions = useCallback(function () {
    // Check if options need to be regenerated
    if (optionsStatus.current === OPTIONS_STATUS.STATIC) {
      return;
    }

    // Test if array
    if (Array.isArray(options)) {
      setAvailableOptions(options);
      optionsStatus.current = OPTIONS_STATUS.STATIC;
      return;
    }

    // If not a function inform developer of improper implementation
    if (typeof options !== 'function') {
      throw new TypeError('options must be an array or function');
    }

    // Get results
    var functionResults = options(searchText);

    // If it is a promise wait for it to finish and use loading state
    if (functionResults.then instanceof Function) {
      var asyncOptionsIndex = latestAsyncOptionsIndex.current + 1;
      latestAsyncOptionsIndex.current = asyncOptionsIndex;
      setIsLoading(true);
      optionsStatus.current = OPTIONS_STATUS.LOADING;
      return functionResults.then(function (returnedOptions) {
        if (autocompleteInputRef === false) return;
        // Prevents stale requests overwriting latest request
        if (latestAsyncOptionsIndex.current === asyncOptionsIndex) {
          var filteredOptions = visibleOptionsHandler(searchText, returnedOptions, allowCustomInputValue, customOptions, allowSelectAll, multiselect);
          setVisibleOptions(filteredOptions);
          setIsLoading(false);
          optionsStatus.current = loadOnce ? OPTIONS_STATUS.STATIC : OPTIONS_STATUS.READY;
        }
      }, function (error) {
        console.error(error);
        setAvailableOptions([]);
        setIsLoading(false);
        optionsStatus.current = OPTIONS_STATUS.FAILED;
      });
    } else if (Array.isArray(functionResults)) {
      var filteredOptions = visibleOptionsHandler(searchText, functionResults, allowCustomInputValue, customOptions, allowSelectAll, multiselect);
      setVisibleOptions(filteredOptions);
      optionsStatus.current = loadOnce ? OPTIONS_STATUS.STATIC : OPTIONS_STATUS.READY;
    }
  }, [allowCustomInputValue, loadOnce, multiselect, options, searchText, visibleOptionsHandler]);

  // Retrieve fresh options any time searchText changes
  // this is for when options are typeof Function
  useEffect(function () {
    if (typeof options === 'function') {
      prepareOptions();
    }
  }, [prepareOptions, searchText]);

  // Input Management
  var handleInputChange = function handleInputChange(event) {
    if (!multiselect) {
      setIsShowingOptions(true);
      setSearchText(event.target.value || '');
    } else {
      setSearchText(event.target.value || '');
    }
  };
  var handleInputFocus = useCallback(function () {
    setIsFocused(true);
    setIsShowingOptions(true);
    setPreferredOption(null);
  }, []);
  var handleInputClear = useCallback(function (event) {
    setSearchText('');
    setValue([]);
    if ((event === null || event === void 0 ? void 0 : event.type) === 'click') {
      autocompleteInputRef.current.focus();
    }
    callUpdate(!!multiselect ? [] : null);
  }, [callUpdate, multiselect]);
  var handleInputBlur = useCallback(function (nextValue) {
    var _visibleOptions$;
    setIsFocused(false);
    if (multiselect) {
      callUpdate(nextValue);
    } else if (allowCustomInputValue && !searchText) {
      handleInputClear();
    } else if (allowCustomInputValue && searchText && (value === null || value === void 0 ? void 0 : value.value) !== ((_visibleOptions$ = visibleOptions[0]) === null || _visibleOptions$ === void 0 ? void 0 : _visibleOptions$.value)) {
      callUpdate(visibleOptions[0]);
    }
  }, [value, allowCustomInputValue, multiselect, callUpdate, handleInputClear, searchText, visibleOptions]);
  var handleOptionRender = useCallback(function (option) {
    return renderOption(option, value, renderOption);
  }, [renderOption, value]);
  var errorHint = error || hint ? '. ' + (error ? 'Error: ' + errorText : 'Hint: ' + hint) + '. ' : '. ';
  var screenReaderText = useMemo(function () {
    if (!isShowingOptions) {
      return '';
    } else if (isShowingOptions && visibleOptions.length !== 0) {
      return label + errorHint + "".concat(visibleOptions.length, " results showing. Use up and down arrow to review options and enter to select or deselect.").concat(selectFirstOnEnter ? ' Press enter without using the up or down arrows to select the first option.' : '');
    } else {
      return noResultsMessageComputed;
    }
  }, [isShowingOptions, noResultsMessageComputed, visibleOptions.length]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Overlay, {
    type: "embedded",
    isVisible: isShowingOptions,
    onClick: handleOptionsClose,
    className: '--autocomplete'
  }), /*#__PURE__*/React.createElement(Input.Label, {
    error: error,
    htmlFor: id,
    disabled: disabled,
    required: required,
    "data-testid": dataTestId ? "".concat(dataTestId, "_label") : null,
    rightContent: rightContent
  }, label), /*#__PURE__*/React.createElement("div", {
    "data-testid": dataTestId ? dataTestId : null,
    className: finalClasses,
    onKeyDown: handleKeyDown,
    onBlur: onBlur
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": dataTestId ? "".concat(dataTestId, "_screenReader") : null,
    role: "alert",
    "aria-live": "polite",
    "aria-label": screenReaderText,
    className: "hc-sr-only"
  }), /*#__PURE__*/React.createElement("div", {
    "data-testid": dataTestId ? "".concat(dataTestId, "_assertiveScreenReader") : null,
    role: "alert",
    "aria-live": "assertive",
    "aria-label": screenReaderRemoveOptionText,
    className: "hc-sr-only"
  }), /*#__PURE__*/React.createElement(AutocompleteInput, {
    id: id,
    "data-testid": dataTestId,
    selectedOption: preferredOption,
    disabled: disabled,
    searchText: searchText,
    renderChip: handleChipRender,
    value: value && ((_value$ = value[0]) === null || _value$ === void 0 ? void 0 : _value$.label) === 'Select All' ? value.slice(1) : value,
    required: required,
    maxLength: maxLength,
    placeholder: displayPlaceholder && placeholder ? placeholder : '',
    onChange: handleInputChange,
    ref: autocompleteInputRef,
    onFocus: handleInputFocus,
    onBlur: function onBlur() {
      return handleInputBlur(value);
    },
    isExpanded: isShowingOptions,
    grow: grow
  }), /*#__PURE__*/React.createElement(IconArea, {
    showSearchIcon: showSearchIcon,
    isLoading: isLoading,
    disabled: disabled,
    searchText: multiselect ? value : searchText,
    isShowingOptions: isShowingOptions,
    onClear: handleInputClear
  }), /*#__PURE__*/React.createElement(OptionsList, {
    allowCustomInputValue: allowCustomInputValue,
    location: location,
    id: id,
    grouped: type === 'grouped',
    optionsListNoMinWidth: optionsListNoMinWidth,
    "data-testid": dataTestId,
    optionSize: optionHeight,
    options: visibleOptions,
    multiselect: multiselect,
    checkedOptions: value,
    selectedOption: preferredOption,
    isLoading: isLoading,
    isShowingOptions: isShowingOptions,
    onOptionClick: handleOptionSelect,
    renderOption: handleOptionRender,
    noResultsMessage: noResultsMessageComputed,
    searchText: searchText,
    filter: filter,
    setSelectedOption: setPreferredOption
  })), !disableFieldInfo && /*#__PURE__*/React.createElement(Input.Info, {
    disabled: disabled,
    error: error
  }, error ? errorText : hint));
};

// Prepackaged filters
var filters = {
  contains: containsFilter,
  fuzzy: fuzzyFilter,
  none: noneFilter,
  startsWith: startsWithFilter
};
Autocomplete.filters = filters;
Autocomplete.displayName = 'Autocomplete';

export { Autocomplete, OPTIONS_STATUS, Autocomplete as default, filters };
